import { STORAGE_KEYS } from '@/config/storage-config';
import type { CleanupTask, TaskDomain } from '@/components/control-panel/control-panel-dummy-data';

export interface AsyncTaskApiResponse {
  task_id: string;
  task_type: string;
  source_service: string;
  agent_id: string | null;
  thread_id: string | null;
  status: 'pending' | 'running' | 'done' | 'error';
  external_id: string | null;
  payload: Record<string, unknown> | null;
  result: Record<string, unknown> | null;
  error_details: Record<string, unknown> | null;
  created_at: string;
  updated_at: string | null;
  started_at: string | null;
  finished_at: string | null;
}

export interface TaskListApiResponse {
  tasks: AsyncTaskApiResponse[];
  total: number;
}

const KNOWN_TASK_TYPES: ReadonlySet<TaskDomain> = new Set<TaskDomain>([
  'quick_discovery',
  'services',
  'integrations',
  'incidents',
  'incident_workflows',
  'aiops',
  'escalation_policies',
  'schedules',
  'users',
  'teams',
  // Legacy task_types still emitted by older threads — keep them mapped so
  // the task list keeps rendering during the rollout window.
  'responders',
  'stakeholders',
  'end',
  'final_report',
]);

const TASK_TYPE_DISPLAY_NAMES: Record<string, string> = {
  quick_discovery: 'Quick Discovery',
  services: 'Services Analysis',
  integrations: 'Integrations Analysis',
  incidents: 'Incidents Analysis',
  incident_workflows: 'Incident Workflows Analysis',
  aiops: 'AIOps Analysis',
  escalation_policies: 'Escalation Policies Analysis',
  schedules: 'Schedules Analysis',
  users: 'Users and Teams Analysis',
  teams: 'Users and Teams Analysis',
  responders: 'Users and Teams Analysis',
  stakeholders: 'Users and Teams Analysis',
  end: 'Optimize & Clean up',
  execute_all_pending_actions: 'Execute Actions',
  final_report: 'Final Report',
};

function taskTypeToDomain(taskType: string): TaskDomain {
  return KNOWN_TASK_TYPES.has(taskType as TaskDomain)
    ? (taskType as TaskDomain)
    : 'services';
}

export function mapApiTaskToCleanupTask(apiTask: AsyncTaskApiResponse): CleanupTask {
  const name = TASK_TYPE_DISPLAY_NAMES[apiTask.task_type] ?? apiTask.task_type;

  return {
    id: apiTask.task_id,
    name,
    description: apiTask.task_type,
    status: apiTask.status,
    domain: taskTypeToDomain(apiTask.task_type),
    startedAt: apiTask.started_at ?? undefined,
    completedAt: apiTask.finished_at ?? undefined,
  };
}

export async function fetchThreadTasks(
  threadId: string,
  baseUrl: string,
): Promise<[Error | null, TaskListApiResponse | null]> {
  const token = localStorage.getItem(STORAGE_KEYS.TOKEN);
  if (!token) {
    return [new Error('No auth token available'), null];
  }

  try {
    const response = await fetch(`${baseUrl}/threads/${threadId}/tasks`, {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      return [new Error(`Failed to fetch tasks: ${response.status}`), null];
    }

    const data: TaskListApiResponse = await response.json();
    return [null, data];
  } catch (error) {
    return [error instanceof Error ? error : new Error(String(error)), null];
  }
}
